import type { Product } from '@/types';

const getPreviousPrice = (
  displayPrice: number,
  rrp?: number,
  wasPrice?: number,
): string | null => {
  const rrpValue = rrp ?? 0;
  const wasValue = wasPrice ?? 0;

  if (displayPrice >= rrpValue && displayPrice >= wasValue) {
    return null;
  }

  const previousPrice = rrpValue > wasValue ? rrpValue : wasValue;
  return `€${previousPrice.toFixed(2)}`;
};

const getPromoBadge = (
  hit: Record<string, any>,
  defaultBadge: string,
): { altText: string; src: string } | null => {
  if (hit.isInPromotion && hit.promotionExpiry) {
    const nowSeconds = Math.floor(Date.now() / 1000);

    if (nowSeconds <= hit.promotionExpiry && hit.promotionBadgeUrl) {
      return { altText: '', src: hit.promotionBadgeUrl };
    }
  }

  if (defaultBadge) {
    return { altText: '', src: defaultBadge };
  }

  return null;
};

export const mapAlgoliaHitToProduct = (
  hit: Record<string, any>,
  defaultPromotionBadge: string,
): Product => ({
  author: hit.author ?? null,
  format: (hit.format as 'Hardback' | 'Paperback') ?? null,
  image: hit.defaultImageUrl
    ? {
        altText: hit.name,
        mainSrc: hit.defaultImageUrl,
        thumbnailSrc: hit.defaultImageUrl,
      }
    : null,
  loyaltyPoints: 0,
  maxQuantity: 50,
  name: hit.name,
  price: {
    current: `€${hit.displayPrice.toFixed(2)}`,
    former: getPreviousPrice(hit.displayPrice, hit.rrp, hit.wasPrice),
    amount: hit.displayPrice,
  },
  // contentType is the Optimizely content-type name ("BookProduct" /
  // "GenericProduct" / "GiftCardProduct"), which matches the ProductType union.
  // hit.productType is an AX category code, not this discriminator.
  productType: (hit.contentType as Product['productType']) ?? null,
  productUrl: hit.url,
  promoBadge: getPromoBadge(hit, defaultPromotionBadge),
  promotion: hit.dispatchMessage || '',
  purchaseAction: hit.purchaseAction as Product['purchaseAction'],
  sku: hit.productCode,
});
